import { refreshInsights } from './assistantService';
import { subscribeDashboardRefresh } from './dashboardRefreshBus';

/**
 * Dedupes POST /insights/refresh across InsightStrip + widget slots.
 * One in-flight promise; one completed key per user/view until dashboard refresh
 * or household change invalidates it.
 */
let inFlight: Promise<void> | null = null;
let completedKey: string | null = null;
/** Bumped on invalidate so an in-flight generate cannot mark stale work as done. */
let epoch = 0;

export function insightRefreshKey(userId: string | undefined, view: string): string {
  return `${userId ?? 'anon'}:${view}`;
}

export function invalidateInsightRefresh(reason?: string): void {
  completedKey = null;
  epoch += 1;
  if (reason) {
    console.info('[insightRefresh] ♻️ invalidated', { reason });
  }
}

/** Subscribe once at module load so imports/join-leave clear the gate. */
subscribeDashboardRefresh((generation) => {
  invalidateInsightRefresh(`dashboard_refresh:${generation}`);
});

/**
 * Ensure insight cards exist / are regenerated at most once per key.
 * Safe to call from strip + multiple widgets in the same paint.
 */
export function ensureInsightsGeneratedOnce(key: string): Promise<void> {
  if (completedKey === key) return Promise.resolve();
  if (inFlight) return inFlight;

  const startedEpoch = epoch;
  inFlight = refreshInsights()
    .then(() => {
      if (startedEpoch === epoch) completedKey = key;
    })
    .catch(() => {
      // Leave completedKey unset so a later caller can retry.
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Force a new generate even when cards already exist (portfolio add/edit/delete).
 * Waits out any in-flight generate so the later write uses the new holdings.
 */
export async function requestInsightsRefresh(key: string, reason?: string): Promise<void> {
  invalidateInsightRefresh(reason);
  const stale = inFlight;
  if (stale) await stale;
  await ensureInsightsGeneratedOnce(key);
}
